import React, { type Ref } from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { panelEntryPoint } from '@atlassian/entry-point-fixtures/panelEntryPoint';
import { PanelProvider, PanelRouter, PanelsRenderer, Trigger } from '@atlassian/panel-system';

import { RelayEnvironmentWrapper } from '../utils/relay-environment-wrapper';

const styles = cssMap({
	layout: {
		height: '100vh',
		width: '100%',
		overflow: 'hidden',
	},
	mainContent: {
		flex: 1,
		minWidth: '0px',
		paddingBlockStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		backgroundColor: token('color.background.neutral.subtle'),
		overflow: 'auto',
	},
	buttonGroup: {
		display: 'flex',
		gap: token('space.100'),
		flexWrap: 'wrap',
	},
	panelWrapper: {
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		flexShrink: 0,
		overflow: 'hidden',
		height: '100vh',
		display: 'flex',
		flexDirection: 'column',
		width: '400px',
	},
});

function EntryPointLoadingExample() {
	return (
		<PanelProvider>
			<Flex xcss={styles.layout}>
				<Box xcss={styles.mainContent}>
					<Stack space="space.300">
						<Heading size="medium">Entry Point Loading Example</Heading>
						<Text>This example demonstrates entry point loading using a simple entry point.</Text>

						<Stack space="space.200">
							<Heading size="small" as="h3">
								Open Panel
							</Heading>
							<Text color="color.text.subtle" size="small">
								Click to open a panel. The panel loads the entry point.
							</Text>
							<Box xcss={styles.buttonGroup}>
								<Trigger panel={panelEntryPoint} params={{}}>
									{({ ref }) => (
										<Button ref={ref as Ref<HTMLButtonElement>} testId="open-panel">
											Open Panel
										</Button>
									)}
								</Trigger>
							</Box>
						</Stack>
					</Stack>
				</Box>

				<PanelsRenderer>
					<PanelRouter
						wrapper={({ children }: { children: React.ReactNode }) => (
							<Box xcss={styles.panelWrapper}>{children}</Box>
						)}
						errorFallback={() => <Text as="p">Error loading panel</Text>}
					/>
				</PanelsRenderer>
			</Flex>
		</PanelProvider>
	);
}

export default function EntryPointLoadingExampleWithProvider(): React.JSX.Element {
	return (
		<RelayEnvironmentWrapper>
			<EntryPointLoadingExample />
		</RelayEnvironmentWrapper>
	);
}
